import { useState, useEffect, useMemo } from 'react';
import { Plus, X, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { storage } from '@/lib/storage';
import { uuid, download } from '@/lib/utils';
import Papa from 'papaparse';
import { toast } from 'sonner';

interface Expense {
  id: string;
  date: string;
  category: string;
  description: string;
  amount: number;
}

const CATEGORIES = [
  'Travel',
  'Food',
  'Office',
  'Software',
  'Marketing',
  'Hardware',
  'Internet',
  'Misc',
];

export function ExpenseTracker() {
  const [items, setItems] = useState<Expense[]>(() =>
    storage.get<Expense[]>('expenses', []),
  );
  const [draft, setDraft] = useState<Expense>({
    id: '',
    date: new Date().toISOString().slice(0, 10),
    category: 'Travel',
    description: '',
    amount: 0,
  });

  useEffect(() => {
    storage.set('expenses', items);
  }, [items]);

  function add() {
    if (!draft.description || draft.amount <= 0) return;
    setItems((arr) => [{ ...draft, id: uuid() }, ...arr]);
    setDraft({
      id: '',
      date: new Date().toISOString().slice(0, 10),
      category: draft.category,
      description: '',
      amount: 0,
    });
  }
  function remove(id: string) {
    setItems((arr) => arr.filter((x) => x.id !== id));
  }

  const totalsByCat = useMemo(() => {
    const map: Record<string, number> = {};
    for (const it of items) {
      map[it.category] = (map[it.category] ?? 0) + it.amount;
    }
    return map;
  }, [items]);
  const total = useMemo(() => items.reduce((s, it) => s + it.amount, 0), [items]);

  function exportCsv() {
    const csv = Papa.unparse(items);
    download(csv, 'expenses.csv', 'text/csv');
    toast.success('Exported');
  }

  return (
    <div>
      <ToolHeader
        toolId="expense-tracker"
        title="Expense tracker"
        description="Quickly log business expenses, categorize, and export to CSV. Stored locally."
        actions={
          <Button size="sm" variant="ghost" onClick={exportCsv} disabled={!items.length}>
            <Download size={11} />
            CSV
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold mb-3">New expense</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Field label="Date">
              <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            </Field>
            <Field label="Category">
              <Select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                options={CATEGORIES.map((c) => ({ value: c, label: c }))}
              />
            </Field>
            <Field label="Description" className="col-span-2 sm:col-span-1">
              <Input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="e.g. Cab to airport"
              />
            </Field>
            <Field label="Amount (₹)">
              <Input
                type="number"
                value={draft.amount || ''}
                onChange={(e) => setDraft({ ...draft, amount: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>
          <Button variant="primary" className="mt-3" onClick={add} disabled={!draft.description || !draft.amount}>
            <Plus size={11} />
            Add expense
          </Button>
        </Card>

        <Card>
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">Total</div>
          <div className="text-3xl font-mono font-semibold mt-1.5 tabular-nums">
            ₹{total.toLocaleString('en-IN')}
          </div>
          <div className="border-t border-border pt-3 mt-3 space-y-1.5 text-xs">
            {Object.entries(totalsByCat)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, v]) => (
                <div key={cat} className="flex items-center justify-between">
                  <span className="text-fg-muted">{cat}</span>
                  <span className="font-mono">₹{v.toLocaleString('en-IN')}</span>
                </div>
              ))}
          </div>
        </Card>
      </div>

      <Card className="!p-0">
        <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
          {items.length} {items.length === 1 ? 'entry' : 'entries'}
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-sm text-fg-subtle text-center">
            No expenses yet. Add one above.
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border last:border-0 group">
                  <td className="px-4 py-2.5 font-mono text-xs text-fg-subtle whitespace-nowrap">
                    {it.date}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 text-2xs rounded-md bg-bg-inset border border-border text-fg-muted">
                      {it.category}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">{it.description}</td>
                  <td className="px-4 py-2.5 font-mono tabular-nums text-right">
                    ₹{it.amount.toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-2.5 w-8">
                    <button
                      onClick={() => remove(it.id)}
                      className="opacity-0 group-hover:opacity-100 transition size-6 rounded text-fg-subtle hover:text-danger hover:bg-surface-hover flex items-center justify-center"
                    >
                      <X size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default ExpenseTracker;
